/**
 * AmbientMusic -- Procedural ambient music system (C418-style)
 *
 * Generates context-specific ambient music using Web Audio API:
 *   - 5 contexts: overworld_day, overworld_night, cave, nether, menu
 *   - 60-90 second procedural tracks with proper chord progressions
 *   - 3-5 minute silence pauses between songs (Minecraft-style)
 *   - Smooth crossfading between contexts (1-2s fade)
 *   - Separate volume control for music
 */

// ---- Musical constants ----

// MIDI note to frequency
function midiToFreq(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

// Utility
function lerp(a, b, t) { return a + (b - a) * t; }
function rand(min, max) { return min + Math.random() * (max - min); }
function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// Note names as MIDI (C4 = 60)
const C3 = 48, D3 = 50, E3 = 52, F3 = 53, G3 = 55, A3 = 57, B3 = 59;
const C4 = 60, D4 = 62, E4 = 64, F4 = 65, G4 = 67, A4 = 69, B4 = 71;
const C5 = 72, D5 = 74, E5 = 76, F5 = 77, G5 = 79, A5 = 81;
const C2 = 36, D2 = 38, E2 = 40, F2 = 41, G2 = 43, A2 = 45, B2 = 47;
const Eb3 = 51, Ab3 = 56, Bb3 = 58;
const Eb4 = 63, Ab4 = 68, Bb4 = 70;
const Eb5 = 75, Ab5 = 80, Bb5 = 82;
const Gb2 = 42, Db3 = 49, Gb3 = 54;

// States for the music state machine
const STATE_IDLE = 'idle';           // silence between tracks
const STATE_PLAYING = 'playing';     // track is playing
const STATE_FADE_OUT = 'fade_out';   // fading out current track
const STATE_GAP = 'gap';             // short silence gap between context switch
const STATE_FADE_IN = 'fade_in';     // fading in new track

const FADE_DURATION = 1.5;  // seconds
const GAP_DURATION = 1.0;   // silence gap between tracks on context switch

// ---- Track generators ----
// Each returns an array of scheduled note events: { time, freq, dur, vol, wave, pan }

/**
 * Overworld Day: Calm, peaceful piano-like melody.
 * Uses C major, F major, G major chords with gentle arpeggios.
 * C418 "Minecraft" / "Sweden" inspired.
 */
function generateOverworldDay() {
  const trackLen = rand(65, 85);
  const events = [];
  let t = 0.5;

  // Chord progression: C - F - G - C, Am - F - G - C (bars of ~8 seconds)
  const chordProgression = [
    { root: C4, notes: [C4, E4, G4], bass: C3 },       // C major
    { root: F3, notes: [F4, A4, C5], bass: F3 },        // F major
    { root: G3, notes: [G4, B4, D5], bass: G3 },        // G major
    { root: C4, notes: [C4, E4, G4], bass: C3 },        // C major
    { root: A3, notes: [A3, C4, E4], bass: A3 },        // A minor
    { root: F3, notes: [F3, A3, C4], bass: F3 },        // F major
    { root: G3, notes: [G3, B3, D4], bass: G3 },        // G major
    { root: C4, notes: [C4, E4, G4], bass: C3 },        // C major
  ];

  // Melody notes that work over C major scale
  const melodyNotes = [C5, D5, E5, G5, A5, G5, E5, D5, C5, E5, G5, C5, D5, E5, F5, E5, D5, C5];

  let chordIdx = 0;
  let melodyIdx = 0;
  const barLen = 7.5;

  while (t < trackLen - 4) {
    const chord = chordProgression[chordIdx % chordProgression.length];

    // --- Soft bass note ---
    events.push({
      time: t,
      freq: midiToFreq(chord.bass),
      dur: barLen * 0.9,
      vol: 0.06,
      wave: 'sine',
      pan: 0,
      attack: 0.3,
      release: 1.5,
    });

    // --- Gentle pad chord (long sustained) ---
    for (let i = 0; i < chord.notes.length; i++) {
      events.push({
        time: t + 0.1 * i,
        freq: midiToFreq(chord.notes[i]),
        dur: barLen * 0.85,
        vol: 0.025,
        wave: 'sine',
        pan: (i - 1) * 0.3,
        attack: 0.8,
        release: 2.0,
      });
    }

    // --- Arpeggio / melody notes (piano-like) ---
    const notesInBar = 3 + Math.floor(Math.random() * 3); // 3-5 notes per bar
    for (let n = 0; n < notesInBar; n++) {
      const noteTime = t + 0.8 + n * rand(1.2, 2.0);
      if (noteTime >= trackLen - 3) break;

      // Choose from chord tones or melody scale, biased toward chord tones
      let noteFreq;
      if (Math.random() < 0.6) {
        // Melody
        noteFreq = midiToFreq(melodyNotes[melodyIdx % melodyNotes.length]);
        melodyIdx++;
      } else {
        // Chord arpeggio
        noteFreq = midiToFreq(pick(chord.notes));
      }

      const noteDur = rand(2.0, 4.5);
      events.push({
        time: noteTime,
        freq: noteFreq,
        dur: noteDur,
        vol: rand(0.04, 0.07),
        wave: 'sine',
        pan: rand(-0.5, 0.5),
        attack: 0.02,
        release: 1.5,
        harmonic: true,
      });
    }

    // Occasional high octave shimmer note
    if (Math.random() < 0.3) {
      const shimmerTime = t + rand(2, barLen - 2);
      if (shimmerTime < trackLen - 3) {
        events.push({
          time: shimmerTime,
          freq: midiToFreq(pick(chord.notes) + 12),
          dur: rand(3, 6),
          vol: 0.015,
          wave: 'sine',
          pan: rand(-0.7, 0.7),
          attack: 1.0,
          release: 2.0,
        });
      }
    }

    t += barLen;
    chordIdx++;
  }

  return { events, duration: trackLen };
}

/**
 * Overworld Night: Slower, atmospheric, minor key, ethereal pads.
 * Uses Am, Em, Dm, F progressions. C418 "Clark" / "Danny" inspired.
 */
function generateOverworldNight() {
  const trackLen = rand(70, 90);
  const events = [];
  let t = 1.0;

  // Minor chord progression: Am - Em - Dm - Am - F - Dm - Em - Am
  const chordProgression = [
    { notes: [A3, C4, E4], bass: A2 },        // Am
    { notes: [E3, G3, B3], bass: E2 },         // Em
    { notes: [D3, F3, A3], bass: D2 },         // Dm
    { notes: [A3, C4, E4], bass: A2 },         // Am
    { notes: [F3, A3, C4], bass: F2 },         // F major
    { notes: [D3, F3, A3], bass: D2 },         // Dm
    { notes: [E3, G3, B3], bass: E2 },         // Em
    { notes: [A3, C4, E4], bass: A2 },         // Am
  ];

  // Minor pentatonic melody in higher register
  const melodyPool = [A4, C5, D5, E5, G5, A5, E5, D5, C5, A4, G4, A4, C5, E5];

  let chordIdx = 0;
  let melodyIdx = 0;
  const barLen = 9.0; // slower bars

  while (t < trackLen - 5) {
    const chord = chordProgression[chordIdx % chordProgression.length];

    // --- Deep bass pad ---
    events.push({
      time: t,
      freq: midiToFreq(chord.bass),
      dur: barLen,
      vol: 0.04,
      wave: 'sine',
      pan: 0,
      attack: 1.5,
      release: 2.5,
    });

    // --- Ethereal pad (wide stereo, long attack) ---
    for (let i = 0; i < chord.notes.length; i++) {
      events.push({
        time: t + rand(0, 0.5),
        freq: midiToFreq(chord.notes[i]),
        dur: barLen * 0.95,
        vol: 0.022,
        wave: 'triangle',
        pan: (i - 1) * 0.5,
        attack: 2.0,
        release: 3.0,
      });
    }

    // --- Sparse melody notes (very gentle, piano-like) ---
    const notesInBar = 1 + Math.floor(Math.random() * 3); // 1-3 notes
    for (let n = 0; n < notesInBar; n++) {
      const noteTime = t + 1.5 + n * rand(2.0, 3.0);
      if (noteTime >= trackLen - 3) break;

      const noteFreq = midiToFreq(melodyPool[melodyIdx % melodyPool.length]);
      melodyIdx++;

      events.push({
        time: noteTime,
        freq: noteFreq,
        dur: rand(3, 6),
        vol: rand(0.03, 0.055),
        wave: 'sine',
        pan: rand(-0.4, 0.4),
        attack: 0.02,
        release: 2.0,
        harmonic: true,
      });
    }

    // Occasional gentle upper octave pad swell
    if (Math.random() < 0.25) {
      const swellTime = t + rand(1, barLen - 3);
      if (swellTime < trackLen - 4) {
        events.push({
          time: swellTime,
          freq: midiToFreq(pick(chord.notes) + 12),
          dur: rand(4, 8),
          vol: 0.012,
          wave: 'sine',
          pan: rand(-0.8, 0.8),
          attack: 2.0,
          release: 3.0,
        });
      }
    }

    t += barLen;
    chordIdx++;
  }

  return { events, duration: trackLen };
}

/**
 * Caves: Deep drones, occasional echoing notes, mysterious atmosphere.
 * Uses sparse minor 2nds, tritones, whole-tone fragments.
 * Inspired by C418 cave ambience.
 */
function generateCave() {
  const trackLen = rand(60, 80);
  const events = [];
  let t = 0.5;

  // Deep drone notes
  const droneNotes = [C2, D2, E2, F2, G2];
  // Mysterious melody notes (sparse, wide intervals)
  const melodyNotes = [C4, Eb4, G4, Bb4, C5, Eb5, G3, Bb3, D4, F4, Ab4];

  // --- Continuous deep drone layer (shifts slowly) ---
  let droneT = 0;
  while (droneT < trackLen - 5) {
    const droneNote = pick(droneNotes);
    const droneDur = rand(12, 25);

    // Main drone
    events.push({
      time: droneT,
      freq: midiToFreq(droneNote),
      dur: droneDur,
      vol: 0.035,
      wave: 'sine',
      pan: rand(-0.3, 0.3),
      attack: 3.0,
      release: 4.0,
    });

    // Subtle dissonant layer (minor 2nd above)
    if (Math.random() < 0.4) {
      events.push({
        time: droneT + rand(1, 3),
        freq: midiToFreq(droneNote + 1),
        dur: droneDur * 0.7,
        vol: 0.012,
        wave: 'sine',
        pan: rand(-0.6, 0.6),
        attack: 4.0,
        release: 3.0,
      });
    }

    droneT += droneDur * rand(0.6, 0.85);
  }

  // --- Echoing melody notes (sparse, ethereal) ---
  t = rand(3, 8);
  while (t < trackLen - 5) {
    const note = pick(melodyNotes);
    const dur = rand(3, 7);

    events.push({
      time: t,
      freq: midiToFreq(note),
      dur: dur,
      vol: rand(0.025, 0.045),
      wave: 'sine',
      pan: rand(-0.7, 0.7),
      attack: 0.02,
      release: 2.5,
      harmonic: true,
      reverb: 0.5, // extra reverb flag
    });

    // Echo repeat (quieter, delayed)
    if (Math.random() < 0.6) {
      events.push({
        time: t + rand(0.3, 0.6),
        freq: midiToFreq(note),
        dur: dur * 0.6,
        vol: rand(0.01, 0.02),
        wave: 'sine',
        pan: rand(-0.9, 0.9),
        attack: 0.02,
        release: 2.0,
      });
    }

    // Occasional pitch-shifted echo (5th above or octave)
    if (Math.random() < 0.2) {
      const interval = pick([7, 12]); // 5th or octave
      events.push({
        time: t + rand(0.8, 1.5),
        freq: midiToFreq(note + interval),
        dur: dur * 0.4,
        vol: 0.01,
        wave: 'sine',
        pan: rand(-0.5, 0.5),
        attack: 0.5,
        release: 2.0,
      });
    }

    t += rand(4, 12); // sparse spacing
  }

  // --- Water drip-like sounds ---
  t = rand(5, 12);
  while (t < trackLen - 2) {
    if (Math.random() < 0.5) {
      const dripFreq = rand(2000, 4500);
      events.push({
        time: t,
        freq: dripFreq,
        dur: 0.15,
        vol: rand(0.008, 0.018),
        wave: 'sine',
        pan: rand(-0.9, 0.9),
        attack: 0.002,
        release: 0.12,
      });
    }
    t += rand(3, 10);
  }

  return { events, duration: trackLen };
}

/**
 * Nether: Dark, aggressive bass drones, dissonant intervals, percussion-like beats.
 * Uses Locrian mode, tritones, minor seconds.
 * Inspired by C418 "Concrete Halls" / "Dead Voxel".
 */
function generateNether() {
  const trackLen = rand(60, 75);
  const events = [];

  // Deep menacing drone base notes
  const bassNotes = [C2, Db3, Gb2, C2, E2];

  // --- Continuous aggressive drone ---
  let droneT = 0;
  while (droneT < trackLen - 5) {
    const bass = pick(bassNotes);
    const droneDur = rand(8, 18);

    // Main bass drone (sawtooth for harshness)
    events.push({
      time: droneT,
      freq: midiToFreq(bass),
      dur: droneDur,
      vol: 0.04,
      wave: 'sawtooth',
      pan: 0,
      attack: 2.0,
      release: 3.0,
      filter: 400, // low-pass to tame the sawtooth
    });

    // Sub-bass sine layer
    events.push({
      time: droneT,
      freq: midiToFreq(bass - 12),
      dur: droneDur,
      vol: 0.05,
      wave: 'sine',
      pan: 0,
      attack: 2.5,
      release: 3.0,
    });

    // Dissonant tritone interval
    if (Math.random() < 0.5) {
      events.push({
        time: droneT + rand(1, 4),
        freq: midiToFreq(bass + 6), // tritone
        dur: droneDur * 0.6,
        vol: 0.02,
        wave: 'sawtooth',
        pan: rand(-0.4, 0.4),
        attack: 3.0,
        release: 2.0,
        filter: 600,
      });
    }

    droneT += droneDur * rand(0.5, 0.8);
  }

  // --- Percussion-like rhythmic pulses ---
  let percT = rand(2, 6);
  while (percT < trackLen - 3) {
    // Cluster of 2-5 hits
    const hits = 2 + Math.floor(Math.random() * 4);
    const tempo = rand(0.3, 0.6); // time between hits

    for (let h = 0; h < hits; h++) {
      const hitTime = percT + h * tempo;
      if (hitTime >= trackLen - 1) break;

      // Low percussion thud
      events.push({
        time: hitTime,
        freq: rand(40, 80),
        dur: 0.15,
        vol: rand(0.03, 0.055),
        wave: 'sine',
        pan: rand(-0.3, 0.3),
        attack: 0.005,
        release: 0.12,
        pitchDrop: true,
      });
    }

    percT += rand(6, 15);
  }

  // --- Eerie high tones (rare, unsettling) ---
  let eT = rand(8, 18);
  while (eT < trackLen - 6) {
    const eNote = pick([Eb5, Ab5, Bb5, E4, Bb4, Gb3]);
    events.push({
      time: eT,
      freq: midiToFreq(eNote),
      dur: rand(4, 9),
      vol: rand(0.008, 0.018),
      wave: 'sine',
      pan: rand(-0.8, 0.8),
      attack: 2.0,
      release: 3.0,
    });

    eT += rand(10, 22);
  }

  // --- Low rumble noise bursts ---
  let rumbleT = rand(5, 12);
  while (rumbleT < trackLen - 4) {
    if (Math.random() < 0.4) {
      events.push({
        time: rumbleT,
        freq: rand(20, 50),
        dur: rand(2, 5),
        vol: 0.025,
        wave: 'sawtooth',
        pan: 0,
        attack: 0.5,
        release: 2.0,
        filter: 150,
      });
    }
    rumbleT += rand(8, 18);
  }

  return { events, duration: trackLen };
}

/**
 * Menu: Gentle, welcoming melody with warm tones.
 * Simple C major progression, soft piano arpeggios.
 * Inspired by C418 "Beginning 2".
 */
function generateMenu() {
  const trackLen = rand(60, 80);
  const events = [];
  let t = 0.3;

  // Warm progression: C - Am - F - G, C - Em - F - G
  const chordProgression = [
    { notes: [C4, E4, G4], bass: C3 },       // C
    { notes: [A3, C4, E4], bass: A2 },        // Am
    { notes: [F3, A3, C4], bass: F2 },        // F
    { notes: [G3, B3, D4], bass: G2 },        // G
    { notes: [C4, E4, G4], bass: C3 },        // C
    { notes: [E3, G3, B3], bass: E2 },        // Em
    { notes: [F3, A3, C4], bass: F2 },        // F
    { notes: [G3, B3, D4], bass: G2 },        // G
  ];

  // Sweet melody pool
  const melodyPool = [E5, D5, C5, G4, A4, B4, C5, D5, E5, G5, F5, E5, D5, C5, B4, C5, E5, G5];

  let chordIdx = 0;
  let melodyIdx = 0;
  const barLen = 8.0;

  while (t < trackLen - 4) {
    const chord = chordProgression[chordIdx % chordProgression.length];

    // --- Warm bass ---
    events.push({
      time: t,
      freq: midiToFreq(chord.bass),
      dur: barLen * 0.9,
      vol: 0.045,
      wave: 'sine',
      pan: 0,
      attack: 0.4,
      release: 2.0,
    });

    // --- Gentle pad ---
    for (let i = 0; i < chord.notes.length; i++) {
      events.push({
        time: t + 0.05 * i,
        freq: midiToFreq(chord.notes[i]),
        dur: barLen * 0.85,
        vol: 0.02,
        wave: 'triangle',
        pan: (i - 1) * 0.25,
        attack: 1.0,
        release: 2.0,
      });
    }

    // --- Melodic arpeggio (piano-like, evenly spaced) ---
    const arpeggioNotes = [...chord.notes, chord.notes[0] + 12]; // add octave
    const notesInBar = 4 + Math.floor(Math.random() * 2);
    const spacing = (barLen - 1.5) / notesInBar;

    for (let n = 0; n < notesInBar; n++) {
      const noteTime = t + 0.5 + n * spacing;
      if (noteTime >= trackLen - 3) break;

      let noteFreq;
      if (Math.random() < 0.55) {
        noteFreq = midiToFreq(melodyPool[melodyIdx % melodyPool.length]);
        melodyIdx++;
      } else {
        const arpNote = arpeggioNotes[n % arpeggioNotes.length];
        noteFreq = midiToFreq(arpNote);
      }

      events.push({
        time: noteTime,
        freq: noteFreq,
        dur: rand(2.5, 4.0),
        vol: rand(0.04, 0.065),
        wave: 'sine',
        pan: rand(-0.35, 0.35),
        attack: 0.02,
        release: 1.8,
        harmonic: true,
      });
    }

    t += barLen;
    chordIdx++;
  }

  return { events, duration: trackLen };
}

// Track generators by context
const TRACK_GENERATORS = {
  overworld_day: generateOverworldDay,
  overworld_night: generateOverworldNight,
  cave: generateCave,
  nether: generateNether,
  menu: generateMenu,
};

// ---- AmbientMusic class ----

export class AmbientMusic {
  constructor(soundManager) {
    this._sm = soundManager;
    this._musicVolume = 0.75;    // music-specific volume (0-1)
    this._masterScale = 0.14;    // scale factor vs SFX
    this._context = 'overworld_day';
    this._pendingContext = null;

    // State machine
    this._state = STATE_IDLE;
    this._stateTimer = 3.0; // start first track after 3s

    // Currently scheduled audio nodes (for cancellation)
    this._activeNodes = [];
    this._trackGain = null; // GainNode for current track (for fading)
    this._trackDuration = 0;
    this._trackElapsed = 0;

    // Fading
    this._fadeTimer = 0;
    this._fadeFrom = 0;
    this._fadeTo = 0;

    // Pause
    this._paused = false;
    this._pausedState = null;

    // Biome / mood tracking (for backwards compatibility with main.js)
    this._mood = 'day';
    this._biome = 'plains';
  }

  /**
   * Set the music context. Triggers crossfade if different from current.
   * @param {'overworld_day'|'overworld_night'|'cave'|'nether'|'menu'} context
   */
  setContext(context) {
    if (!TRACK_GENERATORS[context]) return;
    if (context === this._context && !this._pendingContext) return;

    this._pendingContext = context;

    // If currently playing, initiate fade-out
    if (this._state === STATE_PLAYING) {
      this._state = STATE_FADE_OUT;
      this._stateTimer = FADE_DURATION;
      this._startFade(this._getEffectiveVolume(), 0, FADE_DURATION);
    } else if (this._state === STATE_IDLE) {
      // If idle, switch context and start new track soon
      this._context = context;
      this._pendingContext = null;
      this._stateTimer = rand(0.5, 2.0);
    }
    // If already fading, the pending context will be picked up afterwards
  }

  /**
   * Set music volume (0-1).
   * Also supports the old API: if called with values from main.js onVolume slider (0-1).
   */
  setVolume(vol) {
    // Backwards compatibility: old code called setVolume(val) where val was master volume 0-1
    this._musicVolume = Math.max(0, Math.min(1, vol));
    // Update live gain if playing
    if (this._trackGain && this._state === STATE_PLAYING) {
      const ctx = this._sm && this._sm.ctx;
      if (ctx) {
        this._trackGain.gain.setTargetAtTime(
          this._getEffectiveVolume(), ctx.currentTime, 0.1
        );
      }
    }
  }

  /**
   * Set biome (backwards compatibility with main.js).
   * Maps biome + mood to context internally.
   */
  setBiome(biomeName) {
    this._biome = (biomeName || 'plains').toLowerCase();
  }

  /**
   * Pause music playback.
   */
  pause() {
    if (this._paused) return;
    this._paused = true;
    this._pausedState = this._state;
    // Quickly fade out
    if (this._trackGain && this._sm && this._sm.ctx) {
      const ctx = this._sm.ctx;
      this._trackGain.gain.setTargetAtTime(0, ctx.currentTime, 0.3);
    }
  }

  /**
   * Resume music playback.
   */
  resume() {
    if (!this._paused) return;
    this._paused = false;
    // Restore volume
    if (this._trackGain && this._sm && this._sm.ctx && this._pausedState === STATE_PLAYING) {
      const ctx = this._sm.ctx;
      this._trackGain.gain.setTargetAtTime(
        this._getEffectiveVolume(), ctx.currentTime, 0.5
      );
    }
  }

  /**
   * Toggle playback (backwards compatibility).
   */
  toggle() {
    if (this._paused) {
      this.resume();
    } else {
      this.pause();
    }
  }

  /**
   * Main update loop. Call every frame.
   * @param {number} dt - delta time in seconds
   * @param {boolean} [isNight] - for backwards compat
   * @param {number} [playerY] - for backwards compat
   * @param {boolean} [isUnderground] - for backwards compat
   */
  update(dt, isNight, playerY, isUnderground) {
    if (!this._sm || !this._sm._initialized) return;
    if (this._paused) return;

    // --- Determine context from mood/biome (backwards compatibility) ---
    if (isUnderground) {
      this._mood = 'cave';
    } else if (isNight) {
      this._mood = 'night';
    } else {
      this._mood = 'day';
    }

    let targetContext;
    if (this._biome === 'nether') {
      targetContext = 'nether';
    } else if (this._mood === 'cave') {
      targetContext = 'cave';
    } else if (this._mood === 'night') {
      targetContext = 'overworld_night';
    } else {
      targetContext = 'overworld_day';
    }

    // Auto-switch context based on game state
    if (targetContext !== this._context && !this._pendingContext) {
      this.setContext(targetContext);
    }

    // --- State machine ---
    this._stateTimer -= dt;

    switch (this._state) {
      case STATE_IDLE:
        if (this._stateTimer <= 0) {
          this._startTrack();
        }
        break;

      case STATE_PLAYING:
        this._trackElapsed += dt;
        if (this._trackElapsed >= this._trackDuration) {
          // Track finished naturally - enter silence period
          this._stopAllNodes();
          this._state = STATE_IDLE;
          this._stateTimer = rand(180, 300); // 3-5 minutes silence
        }
        break;

      case STATE_FADE_OUT:
        this._updateFade(dt);
        if (this._stateTimer <= 0) {
          this._stopAllNodes();
          this._state = STATE_GAP;
          this._stateTimer = GAP_DURATION;
          // Apply pending context
          if (this._pendingContext) {
            this._context = this._pendingContext;
            this._pendingContext = null;
          }
        }
        break;

      case STATE_GAP:
        if (this._stateTimer <= 0) {
          this._startTrack();
        }
        break;

      case STATE_FADE_IN:
        this._updateFade(dt);
        if (this._stateTimer <= 0) {
          this._state = STATE_PLAYING;
          // Ensure volume is at target
          if (this._trackGain && this._sm.ctx) {
            this._trackGain.gain.setTargetAtTime(
              this._getEffectiveVolume(), this._sm.ctx.currentTime, 0.05
            );
          }
        }
        break;
    }
  }

  // ---- Internal methods ----

  _getEffectiveVolume() {
    return this._musicVolume * this._masterScale;
  }

  _startFade(from, to, duration) {
    this._fadeTimer = 0;
    this._fadeFrom = from;
    this._fadeTo = to;
    if (this._trackGain && this._sm.ctx) {
      const ctx = this._sm.ctx;
      const t = ctx.currentTime;
      this._trackGain.gain.cancelScheduledValues(t);
      this._trackGain.gain.setValueAtTime(from, t);
      this._trackGain.gain.linearRampToValueAtTime(
        Math.max(to, 0.0001), t + duration
      );
    }
  }

  _updateFade(dt) {
    this._fadeTimer += dt;
  }

  _startTrack() {
    const ctx = this._sm && this._sm.ctx;
    if (!ctx) return;

    // Generate track
    const generator = TRACK_GENERATORS[this._context];
    if (!generator) return;

    const track = generator();
    this._trackDuration = track.duration;
    this._trackElapsed = 0;

    // Create master gain for this track
    this._trackGain = ctx.createGain();
    this._trackGain.gain.setValueAtTime(0.0001, ctx.currentTime);
    this._trackGain.connect(this._sm.masterGain);

    // Schedule all events
    const now = ctx.currentTime;
    this._activeNodes = [];

    for (const ev of track.events) {
      this._scheduleNote(ctx, now, ev);
    }

    // Fade in
    this._state = STATE_FADE_IN;
    this._stateTimer = FADE_DURATION;
    this._startFade(0.0001, this._getEffectiveVolume(), FADE_DURATION);
  }

  _scheduleNote(ctx, baseTime, ev) {
    const t = baseTime + ev.time;
    const dur = ev.dur;
    const vol = ev.vol;
    const attack = ev.attack || 0.02;
    const release = ev.release || 1.0;

    // --- Main oscillator ---
    const osc = ctx.createOscillator();
    osc.type = ev.wave || 'sine';
    osc.frequency.setValueAtTime(ev.freq, t);

    // Pitch drop effect (nether percussion)
    if (ev.pitchDrop) {
      osc.frequency.exponentialRampToValueAtTime(ev.freq * 0.3, t + dur);
    }

    // --- Gain envelope ---
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    // Attack
    gain.gain.linearRampToValueAtTime(vol, t + Math.min(attack, dur * 0.4));
    // Sustain (hold for most of the note)
    const sustainEnd = t + dur - release;
    if (sustainEnd > t + attack) {
      gain.gain.setValueAtTime(vol * 0.85, sustainEnd);
    }
    // Release
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);

    // --- Optional low-pass filter (for sawtooth taming) ---
    let lastNode = gain;
    if (ev.filter) {
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = ev.filter;
      filter.Q.value = 0.7;
      gain.connect(filter);
      lastNode = filter;
    }

    // --- Stereo panning ---
    if (ev.pan && ev.pan !== 0 && ctx.createStereoPanner) {
      const panner = ctx.createStereoPanner();
      panner.pan.setValueAtTime(ev.pan, t);
      lastNode.connect(panner);
      lastNode = panner;
    }

    // Connect to track gain
    osc.connect(gain);
    lastNode.connect(this._trackGain);

    // --- 2nd harmonic for piano-like timbre ---
    let osc2 = null;
    if (ev.harmonic) {
      osc2 = ctx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(ev.freq * 2, t);

      const gain2 = ctx.createGain();
      gain2.gain.setValueAtTime(0, t);
      gain2.gain.linearRampToValueAtTime(vol * 0.12, t + attack);
      gain2.gain.exponentialRampToValueAtTime(0.0001, t + dur * 0.5);

      osc2.connect(gain2);
      if (ev.pan && ev.pan !== 0 && ctx.createStereoPanner) {
        const panner2 = ctx.createStereoPanner();
        panner2.pan.setValueAtTime(ev.pan, t);
        gain2.connect(panner2);
        panner2.connect(this._trackGain);
      } else {
        gain2.connect(this._trackGain);
      }
    }

    // --- Simple delay for reverb feel (especially caves) ---
    if (ev.reverb) {
      const delay = ctx.createDelay(1);
      delay.delayTime.value = rand(0.2, 0.5);
      const delayGain = ctx.createGain();
      delayGain.gain.value = ev.reverb * 0.3;

      lastNode.connect(delay);
      delay.connect(delayGain);
      delayGain.connect(this._trackGain);
    }

    // Start/stop
    osc.start(t);
    osc.stop(t + dur + 0.1);
    this._activeNodes.push(osc);

    if (osc2) {
      osc2.start(t);
      osc2.stop(t + dur * 0.5 + 0.1);
      this._activeNodes.push(osc2);
    }
  }

  _stopAllNodes() {
    const ctx = this._sm && this._sm.ctx;
    if (!ctx) return;

    // Disconnect track gain to silence everything
    if (this._trackGain) {
      try {
        // Quick fade to avoid click
        this._trackGain.gain.setTargetAtTime(0, ctx.currentTime, 0.05);
        // Disconnect after brief fade
        const tg = this._trackGain;
        setTimeout(() => {
          try { tg.disconnect(); } catch (e) { /* already disconnected */ }
        }, 200);
      } catch (e) { /* ignore */ }
      this._trackGain = null;
    }

    // Stop all oscillators
    for (const node of this._activeNodes) {
      try { node.stop(ctx.currentTime + 0.05); } catch (e) { /* already stopped */ }
    }
    this._activeNodes = [];
  }
}
